// Developer Platform — Tab: Usage (auth-service /developer/usage, fed by the gateway's M5
// aggregation). Shows public-API request volume for the tenant over a window: the total, a per-day
// trend, and breakdowns by client, status class and route. Aggregated + PII-free by construction.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import {
  getUsage, isNotEntitled, isNotRolledOut, type UsageSummary,
} from '@/services/developerService';
import { isEntitlementUnavailable } from '@/lib/apiError';
import { FeatureNotEnabledState, RolloutPendingState, errMsg, TemporarilyUnavailableState } from './common';

const WINDOWS = [7, 30, 90];

const statusColor = (sc: string) =>
  sc === '2xx' ? 'text-success' : sc === '5xx' ? 'text-danger' : sc === '4xx' ? 'text-warning-strong' : 'text-muted-foreground';

export const UsageTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [days, setDays] = useState(30);
  const [data, setData] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [rolloutPending, setRolloutPending] = useState(false);
  const [notEnabledMsg, setNotEnabledMsg] = useState<string | null>(null);
  const [unavailableMsg, setUnavailableMsg] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getUsage(days)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => {
        if (!alive) return;
        if (isEntitlementUnavailable(e)) setUnavailableMsg(errMsg(e, ''));
        else if (isNotEntitled(e)) setNotEnabledMsg(errMsg(e, ''));
        else if (isNotRolledOut(e)) setRolloutPending(true);
        else setLoadError(true);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const maxDay = useMemo(() => Math.max(1, ...(data?.byDay || []).map((d) => d.count)), [data]);

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('developer.loading_usage', { defaultValue: 'Loading usage…' })}
      </div>
    );
  }
  if (unavailableMsg !== null) return <TemporarilyUnavailableState message={unavailableMsg} />;
  if (notEnabledMsg !== null) return <FeatureNotEnabledState message={notEnabledMsg} />;
  if (rolloutPending) {
    return <RolloutPendingState message={t('developer.usage_rollout', { defaultValue: 'Usage analytics are being rolled out to your workspace. Check back soon.' })} />;
  }
  if (loadError) {
    return (
      <div className="rounded-lg px-3 py-2 text-sm border bg-danger-soft border-danger text-danger-strong">
        {t('developer.usage_load_error', { defaultValue: 'Could not load usage.' })}
      </div>
    );
  }

  const d = data!;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('developer.usage_subtitle', { defaultValue: 'Public-API request volume for your workspace. Counts are aggregated per day, client, status and route.' })}
        </p>
        <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setDays(w)}
              className={`text-xs px-2.5 py-1 rounded-md ${days === w ? 'bg-muted text-foreground font-medium' : 'text-muted-foreground hover:bg-muted/50'}`}
            >
              {t('developer.usage_window', { defaultValue: '{{n}}d', n: w })}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-foreground" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground tabular-nums">{d.total.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">
                {t('developer.usage_total', { defaultValue: 'requests in the last {{n}} days', n: d.windowDays })}
              </p>
            </div>
          </div>

          {d.byDay.length > 0 && (
            <div className="mt-5 flex items-end gap-1 h-24">
              {d.byDay.map((b) => (
                <div key={b.day} className="flex-1 flex flex-col items-center justify-end group" title={`${b.day}: ${b.count}`}>
                  <div
                    className="w-full rounded-t bg-orange-400/70 group-hover:bg-orange-500 transition-colors"
                    style={{ height: `${Math.max(2, Math.round((b.count / maxDay) * 100))}%` }}
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {d.total === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-muted-foreground text-sm">
            {t('developer.no_usage', { defaultValue: 'No API usage recorded in this window yet.' })}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-2.5 text-xs font-medium text-muted-foreground border-b border-border">
                {t('developer.usage_by_client', { defaultValue: 'By client' })}
              </div>
              <Table>
                <TableBody>
                  {d.byClient.slice(0, 8).map((r) => (
                    <TableRow key={r.clientId}>
                      <TableCell><code className="text-xs font-mono text-foreground">{r.clientId}</code></TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{r.count.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-2.5 text-xs font-medium text-muted-foreground border-b border-border">
                {t('developer.usage_by_status', { defaultValue: 'By status' })}
              </div>
              <Table>
                <TableBody>
                  {d.byStatus.map((r) => (
                    <TableRow key={r.statusClass}>
                      <TableCell><span className={`text-sm font-mono font-medium ${statusColor(r.statusClass)}`}>{r.statusClass}</span></TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{r.count.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-2.5 text-xs font-medium text-muted-foreground border-b border-border">
                {t('developer.usage_by_route', { defaultValue: 'By route' })}
              </div>
              <Table>
                <TableHeader className="sr-only">
                  <TableRow><TableHead>Route</TableHead><TableHead>Count</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {d.byRoute.slice(0, 8).map((r) => (
                    <TableRow key={r.route}>
                      <TableCell><code className="text-xs font-mono text-muted-foreground">{r.route}</code></TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{r.count.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
